import { useState, useEffect } from "react";
import { useTenantConfig, useUpdateTenantConfig } from "@/hooks/useTenants";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import type { TenantConfig } from "@/types";

const defaultConfig: TenantConfig = {
  venue_name: "",
  number_of_simulators: 1,
  country: "",
  city: "",
  state: "",
  zip_code: "",
  address_line_1: "",
  address_line_2: "",
  has_golf_course: false,
  has_bays: true,
  currency: "USD",
  language: "en",
  timezone: "America/New_York",
  hour_format: "12h",
  date_format: "MM/DD/YYYY",
  updated_at: "",
};

export default function TenantConfigTab({ tenantId }: { tenantId: string }) {
  const { data: config, isLoading } = useTenantConfig(tenantId);
  const updateMutation = useUpdateTenantConfig();
  const [form, setForm] = useState<TenantConfig>(defaultConfig);

  useEffect(() => {
    if (config) setForm(config);
  }, [config]);

  const handleSave = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { updated_at, ...data } = form;
      await updateMutation.mutateAsync({ tenantId, data });
      toast.success("Venue configuration saved");
    } catch {
      toast.error("Failed to save configuration");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Venue Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Venue Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Venue Name</label>
              <Input value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Number of Simulators</label>
              <Input type="number" min={0} value={form.number_of_simulators} onChange={(e) => setForm({ ...form, number_of_simulators: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Address Line 1</label>
              <Input value={form.address_line_1} onChange={(e) => setForm({ ...form, address_line_1: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Address Line 2</label>
              <Input value={form.address_line_2} onChange={(e) => setForm({ ...form, address_line_2: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">City</label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">State</label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Zip Code</label>
              <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Country</label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <label className="text-sm font-medium block">Venue Features</label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.has_golf_course} onChange={(e) => setForm({ ...form, has_golf_course: e.target.checked })} className="rounded" />
                Golf Course
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.has_bays} onChange={(e) => setForm({ ...form, has_bays: e.target.checked })} className="rounded" />
                Simulator Bays
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              Select both if the venue offers golf course and simulator bays.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Regional Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Regional Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Currency</label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="CAD">CAD</SelectItem>
                  <SelectItem value="AUD">AUD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Language</label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Timezone</label>
              <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="America/New_York" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Hour Format</label>
              <Select value={form.hour_format} onValueChange={(v) => setForm({ ...form, hour_format: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">12-hour</SelectItem>
                  <SelectItem value="24h">24-hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Date Format</label>
              <Select value={form.date_format} onValueChange={(v) => setForm({ ...form, date_format: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? "Saving..." : "Save Configuration"}
        </Button>
      </div>
    </div>
  );
}
